import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

import { LoadMoreFooter } from '@components/load-more-footer';

import s from './infinite-scroll.scss';

export interface InfiniteScrollProps {
  /**
   * The content to be rendered inside the scrollable container. This should be
   * a list of items that will be appended to as more data is fetched.
   */
  children: ReactNode;
  /**
   * Whether there is more data to fetch. A paginated API response will usually
   * indicate this, either explicitly or derivatively.
   */
  hasMore?: boolean;
  /**
   * When true, the container will assign the parent element as the root of the
   * Intersection Observer. This can be important as the root defines the bounds
   * of the scrollable area in which to react to an intersection. Left unset,
   * the root default to the viewport.
   */
  hasScrollableParent?: boolean;
  /**
   * A callback that will fetch more data. This is executed when the scroll
   * position reaches a threshold near the bottom of the container.
   */
  loadMore: () => void | Promise<void>;
  /**
   * Whether data is currently being fetched. Prevents the callback from firing
   * multiple times while waiting for a response.
   */
  isLoading?: boolean;
  /**
   * A message to display with the loading spinner.
   */
  loadingMessage?: ReactNode;
}

/**
 * A controller for loading data lazily based on the scroll position of the
 * container. It does not know or care about how the data is fetched, only when
 * it can be fetched. Similarly, the component is not concerned with the layout
 * internally, only that the container is scrollable.
 *
 * When the scroll position reaches a threshold near the bottom of the container
 * and there is more data to fetch, a callback is fired to load more data. The
 * new data should be appended and the component will be re-rendered with an
 * increased scroll height. The cycle repeats until there is no more data to
 * load.
 */
export const InfiniteScroll = ({
  children,
  hasMore = false,
  hasScrollableParent = false,
  loadMore,
  loadingMessage,
  isLoading = false,
}: InfiniteScrollProps) => {
  useStyles(s);

  // Ref bound to an arbitrary element at the same level as the children.
  // This can be used to traverse upwards to a scrollable parent.
  const rootLocatorRef = useRef<HTMLDivElement>(null);

  const { ref } = useIntersectionObserver({
    root: hasScrollableParent ? rootLocatorRef.current?.parentElement : null,
    threshold: 0,
    // Freezing re-initializes the observer once thawed, which is how an
    // intersection ignored while busy gets re-checked when the control props
    // change. Keyed on the raw flag: the footer's own debounce would hold this
    // frozen past the fetch it is meant to cover.
    freezeOnceVisible: isLoading || !hasMore,
    onChange: isIntersecting => {
      if (isLoading || !hasMore || !isIntersecting) return;

      // Only request more data when idle, intersecting and more is available
      loadMore();
    },
  });

  return (
    <>
      {children}

      <LoadMoreFooter isLoading={isLoading} message={loadingMessage} />

      <div
        ref={ref}
        className={s.trigger}
        data-testid="infinite-scroll-trigger"
      />
      <div ref={rootLocatorRef} />
    </>
  );
};

export default InfiniteScroll;
